import type { TranslationKey } from '../i18n/translations'
import type { ProductLicense } from '../../features/auth/types'

export type MenuItem = {
  key: string
  labelKey: TranslationKey
  icon?: string
  route: string
  requiresAplAdministrator?: boolean
  requiresAdministrator?: boolean
  permissionCode?: string
  requiresFactoryView?: boolean
  product?: ProductLicense
  locked?: boolean
  moduleLabelKey?: TranslationKey
  children?: MenuItem[]
}

export const menuItems: MenuItem[] = [
  { key: 'home', labelKey: 'menu.home', icon: '🏠', route: '/app/inicio' },
  {
    key: 'production', labelKey: 'menu.production', icon: '🏭', route: '/app/produccion/dashboard', product: 'Production',
    children: [
      { key: 'dashboard', labelKey: 'menu.dashboard', route: '/app/produccion/dashboard' },
      { key: 'shopFloor', labelKey: 'operations.shopFloor' as TranslationKey, route: '/app/produccion/ejecucion', permissionCode: 'productionOrders' },
      { key: 'operators', labelKey: 'operations.operators' as TranslationKey, route: '/app/produccion/operarios', permissionCode: 'productionOrders' },
      { key: 'planning', labelKey: 'menu.planning', route: '/app/planificacion', permissionCode: 'planning' },
      { key: 'mrp', labelKey: 'menu.mrp', route: '/app/mrp', permissionCode: 'mrp', children: [
        { key: 'mrpOverview', labelKey: 'mrp.overview', route: '/app/mrp' },
        { key: 'mrpForecasts', labelKey: 'mrp.forecasts', route: '/app/mrp/previsiones' },
        { key: 'mrpParameters', labelKey: 'mrp.parameters', route: '/app/mrp/parametros' },
        { key: 'mrpProposals', labelKey: 'mrp.proposals', route: '/app/mrp/propuestas' },
        { key: 'mrpRequirements', labelKey: 'mrp.requirements', route: '/app/mrp/necesidades' },
        { key: 'mrpPegging', labelKey: 'mrp.pegging', route: '/app/mrp/pegging' },
        { key: 'mrpExceptions', labelKey: 'mrp.exceptions', route: '/app/mrp/excepciones' },
        { key: 'mrpRuns', labelKey: 'mrp.runs', route: '/app/mrp/ejecuciones' },
      ]},
      { key: 'factoryView', labelKey: 'menu.factoryView', route: '/app/situacion-fabrica', permissionCode: 'factoryView', requiresFactoryView: true },
      {
        key: 'productionOrders', labelKey: 'menu.productionOrders', route: '/app/ordenes-fabricacion', permissionCode: 'productionOrders',
        children: [
          { key: 'productionOrdersOverview', labelKey: 'orders.overview', route: '/app/ordenes-fabricacion' },
          { key: 'productionOrdersList', labelKey: 'orders.title', route: '/app/ordenes-fabricacion/listado' },
        ],
      },
      {
        key: 'subcontracting', labelKey: 'subcontracting.title', route: '/app/subcontratacion', permissionCode: 'subcontracting',
        children: [
          { key: 'subcontractingOverview', labelKey: 'subcontracting.overview', route: '/app/subcontratacion' },
          { key: 'externalOrders', labelKey: 'subcontracting.orders', route: '/app/subcontratacion/ordenes' },
          { key: 'productionSuppliers', labelKey: 'subcontracting.suppliers', route: '/app/subcontratacion/proveedores' },
        ],
      },
      {
        key: 'engineering', labelKey: 'menu.engineering', route: '/app/ingenieria', permissionCode: 'engineering',
        children: [
          { key: 'engineeringOverview', labelKey: 'engineering.overview', route: '/app/ingenieria' },
          { key: 'manufacturingSets', labelKey: 'engineering.sets', route: '/app/ingenieria/conjuntos' },
          { key: 'manufacturingRoutes', labelKey: 'engineering.routings', route: '/app/ingenieria/rutas' },
          { key: 'manufacturingOperations', labelKey: 'engineering.operations', route: '/app/ingenieria/operaciones' },
        ],
      },
      {
        key: 'resources', labelKey: 'menu.resources', route: '/app/recursos', permissionCode: 'capacity',
        children: [
          { key: 'resourcesOverview', labelKey: 'capacity.overview', route: '/app/recursos' },
          { key: 'productionUnits', labelKey: 'capacity.productionUnits', route: '/app/recursos/estructura' },
          { key: 'workCenters', labelKey: 'capacity.workCenters', route: '/app/recursos/centros-trabajo' },
          { key: 'resourceList', labelKey: 'capacity.resources', route: '/app/recursos/listado' },
          { key: 'calendars', labelKey: 'capacity.calendars', route: '/app/recursos/calendarios' },
        ],
      },
      {
        key: 'maintenance', labelKey: 'menu.maintenance', route: '/app/mantenimiento', permissionCode: 'maintenance',
        children: [
          { key: 'maintenanceOverview', labelKey: 'maintenance.overview', route: '/app/mantenimiento' },
          { key: 'maintenanceAssets', labelKey: 'maintenance.assets', route: '/app/mantenimiento/activos' },
          { key: 'maintenancePlans', labelKey: 'maintenance.plans', route: '/app/mantenimiento/planes' },
          { key: 'maintenanceOrders', labelKey: 'maintenance.orders', route: '/app/mantenimiento/ordenes' },
        ],
      },
      { key: 'productionReports', labelKey: 'menu.reports', route: '/app/produccion/informes', permissionCode: 'reports' },
      { key: 'productionConfiguration', labelKey: 'operations.productionConfiguration' as TranslationKey, route: '/app/produccion/configuracion', permissionCode: 'productionOrders' },
    ],
  },
  { key: 'quality', labelKey: 'menu.quality', icon: '✅', route: '/app/calidad', permissionCode: 'quality', product: 'Quality', children: [
    { key:'qualityOverview',labelKey:'quality.overview',route:'/app/calidad' },
    { key:'qualityControlPlans',labelKey:'quality.controlPlans',route:'/app/calidad/planes-control' },
    { key:'qualityCharacteristics',labelKey:'quality.characteristics',route:'/app/calidad/caracteristicas' },
    { key:'qualitySamplingPlans',labelKey:'quality.samplingPlans',route:'/app/calidad/muestreo' },
    { key:'qualityInspections',labelKey:'quality.inspections',route:'/app/calidad/inspecciones' },
    { key:'qualityIssues',labelKey:'quality.issues',route:'/app/calidad/no-conformidades' },
    { key:'qualityActions',labelKey:'quality.actions',route:'/app/calidad/acciones' },
    { key:'qualityClaims',labelKey:'quality.claims',route:'/app/calidad/reclamaciones' },
    { key:'qualityAudits',labelKey:'quality.audits',route:'/app/calidad/auditorias' },
    { key:'qualityEquipment',labelKey:'quality.equipment',route:'/app/calidad/equipos-medicion' },
    { key:'qualityCalibrations',labelKey:'quality.calibrations',route:'/app/calidad/calibraciones' },
    { key:'qualityDocuments',labelKey:'quality.documents',route:'/app/calidad/documentacion' },
    { key:'qualitySuppliers',labelKey:'quality.supplierEvaluations',route:'/app/calidad/proveedores' },
    { key:'qualityIndicators',labelKey:'quality.indicators',route:'/app/calidad/indicadores' },
    { key:'qualityConfiguration',labelKey:'quality.configuration',route:'/app/calidad/configuracion' },
    { key:'qualityReports',labelKey:'menu.reports',route:'/app/calidad/informes' },
  ] },
  {
    key: 'warehouses', labelKey: 'menu.warehouses', icon: '📦', route: '/app/materiales', permissionCode: 'materials', product: 'Warehouses',
    children: [
      { key: 'materialsOverview', labelKey: 'materials.overview', route: '/app/materiales' },
      { key: 'articles', labelKey: 'materials.articles', route: '/app/materiales/articulos' },
      { key: 'units', labelKey: 'materials.units', route: '/app/materiales/unidades' },
      { key: 'families', labelKey: 'materials.families', route: '/app/materiales/familias' },
      { key: 'categories', labelKey: 'materials.categories', route: '/app/materiales/categorias' },
      { key: 'articleTypes', labelKey: 'materials.articleTypes', route: '/app/materiales/tipos-articulo' },
      { key: 'warehousesList', labelKey: 'materials.warehouses', route: '/app/materiales/almacenes' },
      { key: 'lots', labelKey: 'materials.lots', route: '/app/materiales/lotes' },
      { key: 'serials', labelKey: 'materials.serialNumbers', route: '/app/materiales/numeros-serie' },
      { key: 'inventoryCounts', labelKey: 'operations.inventoryCounts' as TranslationKey, route: '/app/materiales/inventarios' },
      { key: 'warehouseTransfers', labelKey: 'operations.warehouseTransfers' as TranslationKey, route: '/app/materiales/transferencias' },
      { key: 'allocationPolicies', labelKey: 'traceability.policies' as TranslationKey, route: '/app/materiales/politicas-asignacion' },
      { key: 'lotAttributes', labelKey: 'traceability.attributes' as TranslationKey, route: '/app/materiales/atributos-lote' },
      { key: 'inventoryCertificates', labelKey: 'traceability.certificates' as TranslationKey, route: '/app/materiales/certificados' },
      { key: 'stockRequirements', labelKey: 'traceability.requirements' as TranslationKey, route: '/app/materiales/requisitos-stock' },
      { key: 'genealogy', labelKey: 'traceability.genealogy' as TranslationKey, route: '/app/materiales/genealogia' },
      { key: 'expiry', labelKey: 'traceability.expiry' as TranslationKey, route: '/app/materiales/caducidades' },
      { key: 'warehouseReports', labelKey: 'menu.reports', route: '/app/materiales/informes' },
    ],
  },
  { key:'sales',labelKey:'menu.sales' as TranslationKey,icon:'💼',route:'/app/ventas',permissionCode:'sales',product:'Sales',children:[
    {key:'salesOverview',labelKey:'sales.overview' as TranslationKey,route:'/app/ventas'},
    {key:'salesCustomers',labelKey:'sales.customers' as TranslationKey,route:'/app/ventas/clientes'},
    {key:'salesQuotations',labelKey:'sales.quotations' as TranslationKey,route:'/app/ventas/ofertas'},
    {key:'salesOrders',labelKey:'sales.orders' as TranslationKey,route:'/app/ventas/pedidos'},
    {key:'salesDeliveries',labelKey:'sales.deliveries' as TranslationKey,route:'/app/ventas/albaranes'},
    {key:'salesPickings',labelKey:'operations.salesPickings' as TranslationKey,route:'/app/ventas/preparacion'},
    {key:'salesReturns',labelKey:'operations.salesReturns' as TranslationKey,route:'/app/ventas/devoluciones'},
    {key:'salesInvoices',labelKey:'sales.invoices' as TranslationKey,route:'/app/ventas/facturas'},
    {key:'salesCollections',labelKey:'sales.collections' as TranslationKey,route:'/app/ventas/cobros'},
    {key:'salesPriceLists',labelKey:'sales.priceLists' as TranslationKey,route:'/app/ventas/tarifas'},
    {key:'salesConfiguration',labelKey:'sales.configuration' as TranslationKey,route:'/app/ventas/configuracion'},
    {key:'salesReports',labelKey:'menu.reports',route:'/app/ventas/informes'},
  ]},
  { key:'purchasing',labelKey:'menu.purchasing' as TranslationKey,icon:'🛒',route:'/app/compras',permissionCode:'purchasing',product:'Purchasing',children:[
    {key:'purchasingOverview',labelKey:'purchasing.overview' as TranslationKey,route:'/app/compras'},
    {key:'purchasingSuppliers',labelKey:'purchasing.suppliers' as TranslationKey,route:'/app/compras/proveedores'},
    {key:'purchasingSupplierContacts',labelKey:'purchasing.supplierContacts' as TranslationKey,route:'/app/compras/contactos-proveedor'},
    {key:'purchasingSupplierAddresses',labelKey:'purchasing.supplierAddresses' as TranslationKey,route:'/app/compras/direcciones-proveedor'},
    {key:'purchasingSupplierArticles',labelKey:'purchasing.supplierArticles' as TranslationKey,route:'/app/compras/articulos-proveedor'},
    {key:'purchasingRequisitions',labelKey:'purchasing.requisitions' as TranslationKey,route:'/app/compras/solicitudes'},
    {key:'purchasingQuotations',labelKey:'purchasing.quotations' as TranslationKey,route:'/app/compras/ofertas'},
    {key:'purchasingComparisons',labelKey:'purchasing.comparisons' as TranslationKey,route:'/app/compras/comparativa'},
    {key:'purchasingOrders',labelKey:'purchasing.orders' as TranslationKey,route:'/app/compras/pedidos'},
    {key:'purchasingApprovals',labelKey:'purchasing.approvals' as TranslationKey,route:'/app/compras/aprobaciones'},
    {key:'purchasingReceipts',labelKey:'purchasing.receipts' as TranslationKey,route:'/app/compras/albaranes'},
    {key:'purchaseMatching',labelKey:'operations.purchaseMatching' as TranslationKey,route:'/app/compras/conciliacion'},
    {key:'purchasingReturns',labelKey:'purchasing.returns' as TranslationKey,route:'/app/compras/devoluciones'},
    {key:'purchasingInvoices',labelKey:'purchasing.invoices' as TranslationKey,route:'/app/compras/facturas'},
    {key:'purchasingPayments',labelKey:'purchasing.payments' as TranslationKey,route:'/app/compras/pagos'},
    {key:'purchasingBudgets',labelKey:'purchasing.budgets' as TranslationKey,route:'/app/compras/presupuestos'},
    {key:'purchasingAdditionalCosts',labelKey:'purchasing.additionalCosts' as TranslationKey,route:'/app/compras/costes-adicionales'},
    {key:'purchasingConfiguration',labelKey:'purchasing.configuration' as TranslationKey,route:'/app/compras/configuracion'},
    {key:'purchasingReports',labelKey:'menu.reports',route:'/app/compras/informes'},
  ]},
  {key:'finance',labelKey:'menu.finance' as TranslationKey,icon:'💶',route:'/app/finanzas',permissionCode:'finance',product:'Finance',children:[
    {key:'financeOverview',labelKey:'finance.overview' as TranslationKey,route:'/app/finanzas'},
    {key:'financeAccounts',labelKey:'finance.accounts' as TranslationKey,route:'/app/finanzas/plan-contable'},
    {key:'financeYears',labelKey:'finance.years' as TranslationKey,route:'/app/finanzas/ejercicios'},
    {key:'financeJournals',labelKey:'finance.journals' as TranslationKey,route:'/app/finanzas/diarios'},
    {key:'financeEntries',labelKey:'finance.entries' as TranslationKey,route:'/app/finanzas/asientos'},
    {key:'financeTaxes',labelKey:'finance.taxes' as TranslationKey,route:'/app/finanzas/impuestos'},
    {key:'financeTreasury',labelKey:'finance.treasury' as TranslationKey,route:'/app/finanzas/tesoreria'},
    {key:'financeBanks',labelKey:'finance.banks' as TranslationKey,route:'/app/finanzas/bancos'},
    {key:'financeSii',labelKey:'finance.sii' as TranslationKey,route:'/app/finanzas/sii'},
    {key:'financeReports',labelKey:'menu.reports',route:'/app/finanzas/informes'},
  ]},
  {key:'crm',labelKey:'menu.crm' as TranslationKey,icon:'🤝',route:'/app/crm',permissionCode:'crm',product:'Crm',children:[
    {key:'crmOverview',labelKey:'crm.overview' as TranslationKey,route:'/app/crm'},
    {key:'crmLeads',labelKey:'crm.leads' as TranslationKey,route:'/app/crm/leads'},
    {key:'crmContacts',labelKey:'crm.contacts' as TranslationKey,route:'/app/crm/contactos'},
    {key:'crmOpportunities',labelKey:'crm.opportunities' as TranslationKey,route:'/app/crm/oportunidades'},
    {key:'crmActivities',labelKey:'crm.activities' as TranslationKey,route:'/app/crm/actividades'},
    {key:'crmCampaigns',labelKey:'crm.campaigns' as TranslationKey,route:'/app/crm/campanas'},
    {key:'crmCampaignMembers',labelKey:'crm.campaignMembers' as TranslationKey,route:'/app/crm/audiencias'},
    {key:'crmConsents',labelKey:'crm.consents' as TranslationKey,route:'/app/crm/consentimientos'},
    {key:'crmPipeline',labelKey:'crm.pipeline' as TranslationKey,route:'/app/crm/embudo'},
    {key:'crmReports',labelKey:'menu.reports',route:'/app/crm/informes'},
  ]},
  { key: 'notifications', labelKey: 'menu.notifications', icon: '🔔', route: '/app/notificaciones', permissionCode: 'notifications' },
  { key: 'documentDesigner', labelKey: 'documentDesigner.moduleTitle' as TranslationKey, icon: '📐', route: '/app/disenador-documentos', requiresAdministrator: true },
  { key: 'help', labelKey: 'menu.help', icon: '❓', route: '/app/ayuda' },
  { key: 'administration', labelKey: 'menu.administration', icon: '🛡️', route: '/app/administracion', requiresAdministrator: true },
  { key: 'aplConfigurations', labelKey: 'menu.aplConfigurations', icon: '🗂️', route: '/apl/configuraciones', requiresAplAdministrator: true },
]

export function flattenMenuItems(items: MenuItem[], moduleLabelKey?: TranslationKey,
  inheritedIcon?: string): MenuItem[] {
  return items.flatMap(item => {
    const module = moduleLabelKey ?? (item.children?.length ? item.labelKey : undefined)
    const resolved = { ...item, moduleLabelKey: module, icon: item.icon ?? inheritedIcon }
    return [resolved, ...flattenMenuItems(item.children ?? [], module, item.icon ?? inheritedIcon)]
  })
}

export function findMenuItem(pathname: string): MenuItem | undefined {
  return flattenMenuItems(menuItems)
    .filter(item => pathname === item.route || pathname.startsWith(`${item.route}/`))
    .sort((left, right) => right.route.length - left.route.length ||
      Number(Boolean(left.children?.length)) - Number(Boolean(right.children?.length)))[0]
}

export function isMenuItemActive(item: MenuItem, pathname: string): boolean {
  if (item.children?.length) {
    return item.children.some(child => isMenuItemActive(child, pathname))
  }
  return pathname === item.route || pathname.startsWith(`${item.route}/`)
}
